import { $, $$, pageData, setLoading } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm, formToObject, readText } from '../lib/form.js';
import { api } from '../lib/api.js';
import { toastError, toastSuccess } from '../lib/toast.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { mountResourceList } from '../lib/resource-list.js';
import { ReactionEditor, type ReactionEntry, type ReactionKind } from '../lib/reaction-editor.js';
import type { RoleOption } from '../lib/role-picker.js';
import { findSelectMenu } from '../lib/select-menu.js';

interface Menu {
  _id: string;
  name: string;
  channelId: string;
  mode: string;
  useEmbed: boolean;
  title: string;
  content: string;
  color: string;
  allowedRoleIds: string[];
  blockedRoleIds: string[];
  reactions: ReactionEntry[];
}

interface EditorPageData {
  menu: Menu;
  roles: RoleOption[];
  guildId: string;
}

/** The list: all it keeps is republishing. */
export function initReactionsListPage(): void {
  const list = document.querySelector('#menu-list');
  if (!list) return;

  const { guildId } = pageData<{ guildId: string }>();

  mountResourceList({
    endpoint: 'reactions',
    prefix: 'menu',
    guildId,
    listSelector: '#menu-list',
    successMessage: ct('menuSaved', 'Menu saved and published.'),
  });
}

/** Editing a role menu, on a page of its own. */
export function initReactionsEditorPage(): void {
  const form = $<HTMLFormElement>('[data-menu-form]');
  const list = $('[data-reaction-list]');
  if (!form || !list) return;

  const { menu, roles, guildId } = pageData<EditorPageData>();
  const editor = new ReactionEditor(list, roles, $('[data-reaction-empty]'));

  for (const button of $$<HTMLButtonElement>('[data-add-reaction]')) {
    button.addEventListener('click', () => editor.add(button.dataset.addReaction as ReactionKind));
  }

  fillForm(form, menu as unknown as Record<string, unknown>);
  editor.load(menu.reactions);

  /*
   * **Every** menu goes through the component, not only the role lists.
   *
   * A `<select>` replaced by `upgradeSelects` is no longer one: `fillForm`
   * writes into the hidden field the component leaves behind, and the display
   * does not move. The channel and the behaviour therefore stayed on their
   * first value when an existing menu was opened — the page looked empty when
   * it was full.
   */
  for (const [field, value] of [
    ['channelId', menu.channelId ? [menu.channelId] : []],
    ['mode', menu.mode ? [menu.mode] : []],
    ['allowedRoleIds', menu.allowedRoleIds],
    ['blockedRoleIds', menu.blockedRoleIds],
  ] as const) {
    const component = findSelectMenu(form, field);
    if (component) component.value = [...value];
  }

  /*
   * Reading a menu another bot published.
   *
   * The only piece of a competitor's configuration that genuinely transfers:
   * the message is on Discord and so are its emojis. The roles are a suggestion
   * read from what the message says — the rows are filled in, nothing is saved,
   * and the administrator confirms what they see.
   */
  const inspect = $<HTMLButtonElement>('[data-adopt-inspect]');
  inspect?.addEventListener('click', () => {
    void (async () => {
      const link = readText(form, 'adoptLink');
      if (!link) return;

      setLoading(inspect, true);

      try {
        const answer = await api.post<{ entries: { emoji: string; suggestedRoleId: string }[] }>(
          `/api/reactions/inspect?guildId=${guildId}`,
          { guildId, link },
        );

        if (answer.entries.length === 0) {
          toastError(ct('adoptEmpty', 'That message carries no reaction to import.'));
          return;
        }

        editor.load(
          answer.entries.map((entry) => ({
            kind: 'emoji' as const,
            emoji: entry.emoji,
            roleId: entry.suggestedRoleId,
            label: '',
            style: 'secondary' as const,
            placeholder: '',
            options: [],
          })),
        );

        toastSuccess(
          ct('adoptRead', '{count} reactions read. Check the roles before saving.', {
            count: String(answer.entries.length),
          }),
        );
      } catch (error) {
        toastError(error);
      } finally {
        setLoading(inspect, false);
      }
    })();
  });

  mountResourceEditor({
    endpoint: 'reactions',
    listUrl: `/modules/reactions?guildId=${guildId}`,
    guildId,
    form,
    itemId: menu._id,

    serialize: () => {
      const values = formToObject(form);
      delete values.menuId;
      // The reactions live outside the form: the editor rebuilds them from the
      // DOM.
      return {
        ...values,
        allowedRoleIds: findSelectMenu(form, 'allowedRoleIds')?.value ?? [],
        blockedRoleIds: findSelectMenu(form, 'blockedRoleIds')?.value ?? [],
        reactions: editor.collect(),
      };
    },

    labels: {
      saved: ct('menuSaved', 'Menu saved and published.'),
      confirmDelete: ct(
        'confirmDeleteMenu',
        'Delete this menu? The published message will be removed too.',
      ),
      deleted: ct('menuDeleted', 'Menu deleted.'),
    },
  });
}
